import PropTypes from 'prop-types';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Typography, Stack } from '@mui/material';
import { generateReasonsLabel } from 'src/utils/getReferralData';
import { followUpSelection } from 'src/constants/appointmentReason';
// hooks
import { FormProvider, RHFTextField } from '../hook-form';
// component
import { ArchivedAutocomplete } from '../AutoComplete';
import Iconify from '../Iconify';
// store
import { dispatch, useSelector } from '../../redux/store';
import { ArchivedLeadDashBoard, saveNewLeadDetail, updateCompleteStatus } from '../../redux/slices/lead';
// style
// validation
import { CreateLeadArchiveSchema } from '../../validations/validations';

// -----------------------------------------------------------------------------------------------------

const Archive = forwardRef(({ handleClose, type, user, screenType, isDuplicate, setForceUpdate, handleReloadLeads }, ref) => {

    const { tableRowLead, createLead } = useSelector((state) => state.lead);

    const [isCommunication, setCommunication] = useState(user?.openCommunication || tableRowLead?.openCommunication || 'No');

    const { archiveReasons } = useSelector((state) => state.schema);

    const defaultValues = {
        archiveReason: '',
        typed: type
    };

    const [newArchiveReasons, setNewArchiveReasons] = useState([]);
    useEffect(() => {
        let reasonsList = archiveReasons;
        if (reasonsList && reasonsList?.length > 0) {
            if (typeof reasonsList === 'string') {
                try { reasonsList = JSON.parse(reasonsList); } catch { reasonsList = []; }
            }
            const newArchive = reasonsList?.filter?.((value) => value !== 'UNABLE_TO_CONTACT');
            const archive = newArchive?.sort()?.map?.((value) => ({ label: value === 'WANT_NHS' ? 'Want NHS' : generateReasonsLabel(value), value }))
            setNewArchiveReasons(archive);
        }
    }, [archiveReasons]);

    // mode: 'onSubmit' — validation only runs when user clicks Archive, not on dialog
    // open. The old default mode validated on mount, showing "Please select an archive
    // reason" immediately before user interaction (REACT-DA, 6 users, 17 events).
    const methods = useForm({
        resolver: yupResolver(CreateLeadArchiveSchema),
        defaultValues,
        mode: 'onSubmit',
    });

    const {
        handleSubmit,
        watch,
        setValue,
    } = methods;

    const values = watch();

    const ARCHIVE_REASON_ICONS = {
        ANOTHER_PRACTICE: 'mdi:hospital-building',
        APPOINTMENT_RELATED: 'mdi:calendar-remove-outline',
        CHANGED_MIND: 'mdi:head-sync-outline',
        CLINICALLY_NOT_SUITABLE: 'mdi:stethoscope',
        DUPLICATE_ENTRY: 'mdi:content-copy',
        NO_RESPONSE: 'mdi:phone-off-outline',
        OTHER: 'mdi:dots-horizontal-circle-outline',
        PRACTICE_TOO_FAR: 'mdi:map-marker-off-outline',
        TOO_EXPENSIVE: 'mdi:currency-gbp',
        TREATMENT_RELATED: 'mdi:tooth-outline',
        WANT_NHS: 'mdi:shield-cross-outline',
    };

    useImperativeHandle(
        ref,
        () => ({
            async submit(status) {
                await handleSubmit((data) => onSubmit(data, status))();
            },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isCommunication, createLead]
    );

    const onSubmit = async (data, status) => {
        const addNotesObj = {
            note: data.archiveReason,
            treatment: createLead?.lead_treatment?.id || tableRowLead?.treatment_id || user?.treatment_id || null,
            additional_notes: data.archiveNotes
        };
        const archiveData = {
            note: data.archiveNotes
        };
        const openCommunication = {
            id: createLead?.id || tableRowLead?.lead_id || user?.lead_id || '',
            is_open_for_communication: isCommunication === 'Yes',
        };
        dispatch(saveNewLeadDetail({
            archiveLead: {
                notes: addNotesObj,
                openCommunication,
                archiveData,
            }
        }));
        if (isDuplicate) {
            dispatch(ArchivedLeadDashBoard(handleClose, user));
            // Deferred reload — Celery processes the archive asynchronously.
            // Wait 3s so the table refresh hits after the worker drains.
            setTimeout(() => handleReloadLeads?.(), 3000);
            return;
        }
        if (status?.toLowerCase?.() === 'finish') {
            dispatch(updateCompleteStatus(true));
            if (typeof setForceUpdate === 'function') {
                setForceUpdate((prev) => !prev);
            }
            return;
        }
        handleClose();
    };

    const macInput = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            bgcolor: '#ffffff',
            fontSize: '13px',
            '& fieldset': { borderColor: 'rgba(0,0,0,0.12)' },
            '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.20)' },
            '&.Mui-focused fieldset': { borderColor: '#0071e3', borderWidth: '1.5px' },
        },
    };
    const macLabel = { fontSize: '11px', color: '#6e6e73', letterSpacing: '-0.005em', mb: 0.75, textTransform: 'none', fontWeight: 500 };

    return (
        <Box sx={{ width: '100%' }}>
            <Typography sx={{ fontSize: '17px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.022em', mb: 0.5 }}>
                {type !== 'RestoreArchived' ? 'Archive this lead' : 'Restore this lead'}
            </Typography>
            <Typography sx={{ fontSize: '13px', color: '#6e6e73', letterSpacing: '-0.01em', mb: 2.5 }}>
                {type !== 'RestoreArchived'
                    ? 'Give a reason and any context so the next person has clarity.'
                    : 'Note the reason for bringing this lead back into the active list.'}
            </Typography>
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                {type !== 'RestoreArchived' && (
                    <Box sx={{ mb: 2.25 }}>
                        <Typography sx={macLabel}>Why are you archiving?</Typography>
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                            gap: 1,
                        }}>
                            {newArchiveReasons?.filter(reason => reason.value !== 'NHS_WAITING_LIST' && !followUpSelection.includes(reason.value))?.map((opt) => {
                                const selected = values.archiveReason === opt.value;
                                return (
                                    <Box
                                        key={opt.value}
                                        onClick={() => setValue('archiveReason', opt.value, { shouldValidate: true })}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 0.75,
                                            py: 1.5,
                                            px: 1,
                                            borderRadius: '10px',
                                            cursor: 'pointer',
                                            border: '1.5px solid',
                                            borderColor: selected ? '#FF5630' : 'rgba(0,0,0,0.10)',
                                            bgcolor: selected ? 'rgba(255,86,48,0.06)' : '#ffffff',
                                            transition: 'border-color 250ms cubic-bezier(0.32, 0.72, 0, 1), background-color 250ms cubic-bezier(0.32, 0.72, 0, 1), transform 350ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                                            userSelect: 'none',
                                            '&:hover': {
                                                borderColor: selected ? '#FF5630' : 'rgba(0,0,0,0.18)',
                                                bgcolor: selected ? 'rgba(255,86,48,0.06)' : 'rgba(0,0,0,0.02)',
                                            },
                                            '&:active': { transform: 'scale(0.97)' },
                                        }}
                                    >
                                        <Iconify
                                            icon={ARCHIVE_REASON_ICONS[opt.value] || 'mdi:help-circle-outline'}
                                            width={22}
                                            height={22}
                                            sx={{ color: selected ? '#FF5630' : '#6e6e73' }}
                                        />
                                        <Typography sx={{
                                            fontSize: '12px',
                                            fontWeight: selected ? 600 : 500,
                                            color: selected ? '#1d1d1f' : '#6e6e73',
                                            textAlign: 'center',
                                            letterSpacing: '-0.01em',
                                            lineHeight: 1.25,
                                        }}>
                                            {opt.label}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                )}
                <Box sx={{ mb: 2.25 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                        <Typography sx={macLabel} component="span">Notes</Typography>
                        <Box component="span" sx={{
                            fontSize: '10px',
                            fontWeight: 600,
                            color: values.archiveNotes ? '#34c759' : '#ff9500',
                            bgcolor: values.archiveNotes ? 'rgba(52,199,89,0.10)' : 'rgba(255,149,0,0.10)',
                            px: 0.75,
                            py: 0.125,
                            borderRadius: '4px',
                            letterSpacing: '0.02em',
                            textTransform: 'uppercase',
                            mb: 0.75,
                        }}>
                            {values.archiveNotes ? 'Done' : 'Required'}
                        </Box>
                    </Stack>
                    <RHFTextField
                        sx={macInput}
                        name="archiveNotes"
                        multiline
                        rows={4}
                        placeholder={type !== 'RestoreArchived' ? 'Why are we archiving this lead?' : 'Why are we restoring this lead?'}
                    />
                </Box>
                {type !== 'RestoreArchived' && <>
                    <Box sx={{ mb: 2 }}>
                        <Typography sx={macLabel}>Open to future contact?</Typography>
                        <Box sx={{
                            display: 'inline-flex',
                            bgcolor: 'rgba(118, 118, 128, 0.12)',
                            borderRadius: '9px',
                            p: '2px',
                            gap: '2px',
                        }}>
                            {[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }].map((options) => {
                                const sel = isCommunication === options.value;
                                return (
                                    <Box
                                        key={options.value}
                                        onClick={() => setCommunication(options.value)}
                                        sx={{
                                            px: 2,
                                            py: 0.75,
                                            borderRadius: '7px',
                                            cursor: 'pointer',
                                            bgcolor: sel ? '#ffffff' : 'transparent',
                                            boxShadow: sel ? '0 1px 2px rgba(0,0,0,0.10), 0 0 0 0.5px rgba(0,0,0,0.04)' : 'none',
                                            transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
                                            userSelect: 'none',
                                            fontSize: '13px',
                                            fontWeight: sel ? 600 : 500,
                                            color: sel ? '#1d1d1f' : '#6e6e73',
                                            letterSpacing: '-0.01em',
                                            minWidth: '52px',
                                            textAlign: 'center',
                                        }}
                                    >
                                        {options.label}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                    {screenType !== 'CreatedLead' && (
                        <Box sx={{ mb: 1 }}>
                            <Typography sx={macLabel}>Link to similar lead (optional)</Typography>
                            <ArchivedAutocomplete user={user || tableRowLead} />
                        </Box>
                    )}
                </>}
            </FormProvider>
        </Box>
    );
});

Archive.propTypes = {
    handleClose: PropTypes.func,
    type: PropTypes.string,
    user: PropTypes.any,
    screenType: PropTypes.object,
    isDuplicate: PropTypes.bool,
    setForceUpdate: PropTypes.any,
    /** Called ~3s after archive dispatched so table refresh catches Celery drain. */
    handleReloadLeads: PropTypes.func,
};

export default Archive;